import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getAdminMediaQualityDashboard,
  getAdminMediaQualityIssues,
  recheckAdminMediaItem,
  rescoreAdminMediaItem,
} from "../../../../services/midpointApi";
import { CardError, CardLoading } from "../../intelligence/CardState";
import AdminPageLayout from "../layout/AdminPageLayout";
import AdminPageHeader from "../layout/AdminPageHeader";
import AdminBtn from "../layout/AdminBtn";
import AdminTableFooter from "../layout/AdminTableFooter";
import RealEstateAdminNav, { buildRealEstateAdminNavLinks } from "../layout/RealEstateAdminNav";

const ISSUE_PRESETS = [
  {
    key: "missing-media",
    emoji: "🖼️",
    label: "Missing images",
    description: "Listings with no photos at all",
  },
  {
    key: "broken-media",
    emoji: "💔",
    label: "Broken",
    description: "Images that fail to load or validate",
  },
  {
    key: "low-quality-media",
    emoji: "📉",
    label: "Low quality",
    description: "Not sharp or large enough for cards",
  },
  {
    key: "primary-issues",
    emoji: "⭐",
    label: "Primary image",
    description: "Hero image problems on listings",
  },
  {
    key: "uncached-media",
    emoji: "☁️",
    label: "Uncached",
    description: "Not stored in the image cache yet",
  },
  {
    key: "unscored-media",
    emoji: "❓",
    label: "Unscored",
    description: "Awaiting a quality score",
  },
  {
    key: "duplicates",
    emoji: "👯",
    label: "Duplicates",
    description: "Same image attached more than once",
  },
  {
    key: "placeholders",
    emoji: "🎭",
    label: "Placeholders",
    description: "Likely stock or filler images",
  },
];

const GRADE_META = {
  excellent: { emoji: "🌟", label: "Excellent", tone: "excellent" },
  good: { emoji: "✅", label: "Good", tone: "good" },
  acceptable: { emoji: "👍", label: "Acceptable", tone: "acceptable" },
  poor: { emoji: "📉", label: "Poor", tone: "poor" },
  unusable: { emoji: "🚫", label: "Unusable", tone: "unusable" },
};

function fmt(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString();
}

function pct(part, total) {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}

function healthEmoji(score) {
  if (score == null) return "⚪";
  if (score >= 80) return "🟢";
  if (score >= 60) return "🟡";
  return "🔴";
}

function resolveGradeMeta(grade) {
  const key = String(grade || "").toLowerCase();
  return GRADE_META[key] || { emoji: "·", label: grade || "Unknown", tone: "neutral" };
}

function countAttention(summary, primaryImageIssues) {
  if (!summary) return 0;
  const primaryTotal =
    (primaryImageIssues?.brokenPrimary || 0) +
    (primaryImageIssues?.lowQualityPrimary || 0) +
    (primaryImageIssues?.primaryNotCardEligible || 0) +
    (primaryImageIssues?.multiplePrimaries || 0);

  return (
    (summary.propertiesWithoutMedia || 0) +
    (summary.brokenMediaRows || 0) +
    (summary.notCardEligibleRows || 0) +
    primaryTotal
  );
}

function HealthBanner({ summary, primaryImageIssues, onOpenImageHealth, onOpenImageCache }) {
  const avgScore = summary?.averageQualityScore != null ? Number(summary.averageQualityScore) : null;
  const attention = countAttention(summary, primaryImageIssues);
  const totalMedia = summary?.totalMediaRows ?? 0;
  const broken = summary?.brokenMediaRows ?? 0;
  const healthyPct = totalMedia > 0 ? pct(Math.max(totalMedia - broken, 0), totalMedia) : null;

  return (
    <section className="mq-hero panel-card">
      <div className="mq-hero__main">
        <div className="mq-hero__score">
          <span className="mq-hero__score-emoji" aria-hidden="true">
            {healthEmoji(avgScore)}
          </span>
          <div>
            <p className="mq-hero__eyebrow">Average quality score</p>
            <strong className="mq-hero__value">
              {avgScore != null ? avgScore.toFixed(1) : "—"}
            </strong>
            <p className="mq-hero__sub">
              {healthyPct != null
                ? `${healthyPct}% of ${fmt(totalMedia)} images are not broken`
                : `${fmt(totalMedia)} images tracked`}
            </p>
          </div>
        </div>
        <p className="mq-hero__explain">
          Buyers judge listings by photos first. Missing, broken, or low-quality images hurt trust
          on CasaMapa cards — fix primary and broken issues before cache and scoring cleanup.
        </p>
      </div>
      <div className="mq-hero__actions">
        {attention > 0 ? (
          <p className="mq-hero__attention">
            <span aria-hidden="true">👀</span> {fmt(attention)} issue
            {attention === 1 ? "" : "s"} worth reviewing
          </p>
        ) : (
          <p className="mq-hero__attention mq-hero__attention--good">
            <span aria-hidden="true">✨</span> Image health looks good
          </p>
        )}
        <div className="mq-hero__action-row">
          {onOpenImageHealth ? (
            <AdminBtn variant="primary" onClick={onOpenImageHealth}>
              🩺 Image health tools
            </AdminBtn>
          ) : null}
          {onOpenImageCache ? (
            <AdminBtn variant="secondary" onClick={onOpenImageCache}>
              ☁️ Image cache
            </AdminBtn>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function KpiStrip({ summary, primaryImageIssues, activeTab, onSelectTab }) {
  const primaryTotal =
    (primaryImageIssues?.brokenPrimary || 0) +
    (primaryImageIssues?.lowQualityPrimary || 0) +
    (primaryImageIssues?.primaryNotCardEligible || 0) +
    (primaryImageIssues?.multiplePrimaries || 0);

  const cards = [
    {
      emoji: "📷",
      label: "Total images",
      value: fmt(summary?.totalMediaRows),
      readOnly: true,
    },
    {
      key: "missing-media",
      emoji: "🖼️",
      label: "Missing",
      value: fmt(summary?.propertiesWithoutMedia),
      tone: (summary?.propertiesWithoutMedia ?? 0) > 0 ? "bad" : null,
    },
    {
      key: "broken-media",
      emoji: "💔",
      label: "Broken",
      value: fmt(summary?.brokenMediaRows),
      tone: (summary?.brokenMediaRows ?? 0) > 0 ? "bad" : null,
    },
    {
      key: "low-quality-media",
      emoji: "📉",
      label: "Not card-ready",
      value: fmt(summary?.notCardEligibleRows),
      tone: (summary?.notCardEligibleRows ?? 0) > 0 ? "warn" : null,
    },
    {
      key: "primary-issues",
      emoji: "⭐",
      label: "Primary issues",
      value: fmt(primaryTotal),
      tone: primaryTotal > 0 ? "warn" : null,
    },
    {
      key: "uncached-media",
      emoji: "☁️",
      label: "Uncached",
      value: fmt(summary?.uncachedMediaRows),
    },
    {
      key: "unscored-media",
      emoji: "❓",
      label: "Unscored",
      value: fmt(summary?.unscoredMediaRows),
    },
  ];

  return (
    <section className="mq-kpis" aria-label="Image quality summary">
      {cards.map((card) => {
        const className = `mq-kpi panel-card${card.readOnly ? " mq-kpi--readonly" : ""}${!card.readOnly && activeTab === card.key ? " mq-kpi--active" : ""}${card.tone ? ` mq-kpi--${card.tone}` : ""}`;
        const content = (
          <>
            <span className="mq-kpi__emoji" aria-hidden="true">
              {card.emoji}
            </span>
            <span className="mq-kpi__label">{card.label}</span>
            <strong className="mq-kpi__value">{card.value}</strong>
          </>
        );

        if (card.readOnly) {
          return (
            <div key={card.label} className={className}>
              {content}
            </div>
          );
        }

        return (
          <button
            key={card.key}
            type="button"
            className={className}
            onClick={() => onSelectTab(card.key)}
          >
            {content}
          </button>
        );
      })}
    </section>
  );
}

function QualityDistribution({ distribution }) {
  if (!distribution) return null;

  const grades = ["excellent", "good", "acceptable", "poor", "unusable"];
  const total = grades.reduce((sum, key) => sum + (distribution[key] || 0), 0);
  if (!total) return null;

  return (
    <section className="mq-breakdown panel-card">
      <h2 className="mq-section-title">
        <span aria-hidden="true">📊</span> Quality grades
      </h2>
      <div className="mq-breakdown__bar" role="img" aria-label="Image quality grade distribution">
        {grades.map((key) => {
          const count = distribution[key] || 0;
          if (!count) return null;
          const meta = GRADE_META[key];
          return (
            <div
              key={key}
              className={`mq-breakdown__seg mq-breakdown__seg--${meta.tone}`}
              style={{ width: `${Math.max(pct(count, total), 4)}%` }}
              title={`${meta.label}: ${count}`}
            />
          );
        })}
      </div>
      <ul className="mq-breakdown__legend">
        {grades.map((key) => {
          const count = distribution[key] || 0;
          if (!count) return null;
          const meta = GRADE_META[key];
          return (
            <li key={key}>
              <span aria-hidden="true">{meta.emoji}</span>
              {meta.label}
              <strong>{fmt(count)}</strong>
              <span className="mq-breakdown__pct">({pct(count, total)}%)</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function SourceBreakdown({ sources, onFilterSource }) {
  if (!sources?.length) return null;

  return (
    <section className="mq-sources panel-card">
      <h2 className="mq-section-title">
        <span aria-hidden="true">🏢</span> By broker source
      </h2>
      <div className="mq-sources__table-wrap">
        <table className="mq-sources__table">
          <thead>
            <tr>
              <th>Source</th>
              <th>Health</th>
              <th>Media</th>
              <th>Missing</th>
              <th>Broken</th>
              <th>Avg score</th>
            </tr>
          </thead>
          <tbody>
            {sources.map((source) => {
              const mediaRows = source.mediaRows || 0;
              const broken = source.broken || 0;
              const healthyRate = mediaRows > 0 ? pct(Math.max(mediaRows - broken, 0), mediaRows) : 0;
              const sourceKey = source.sourceKey || source.sourceCode;

              return (
                <tr key={sourceKey}>
                  <td>
                    <button
                      type="button"
                      className="mq-sources__name"
                      onClick={() => onFilterSource(sourceKey)}
                    >
                      {sourceKey}
                    </button>
                  </td>
                  <td>
                    <div className="mq-sources__health">
                      <div className="mq-sources__health-track">
                        <div
                          className="mq-sources__health-fill"
                          style={{ width: `${healthyRate}%` }}
                        />
                      </div>
                      <span>{healthyRate}% OK</span>
                    </div>
                  </td>
                  <td>{fmt(mediaRows)}</td>
                  <td className={source.propertiesWithoutMedia > 0 ? "mq-cell--warn" : ""}>
                    {fmt(source.propertiesWithoutMedia)}
                  </td>
                  <td className={broken > 0 ? "mq-cell--bad" : ""}>{fmt(broken)}</td>
                  <td>{source.avgQuality != null ? Number(source.avgQuality).toFixed(1) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ClientViewBreakdown({ clientViews }) {
  if (!clientViews?.length) return null;

  return (
    <section className="mq-client-views panel-card">
      <h2 className="mq-section-title">
        <span aria-hidden="true">🗺️</span> By client view
      </h2>
      <div className="mq-client-views__grid">
        {clientViews.map((view) => {
          const missing = Math.max(0, (view.totalListings || 0) - (view.listingsWithMedia || 0));
          return (
            <article key={view.clientViewId} className="mq-client-view-card">
              <h3>{view.name}</h3>
              <p className="mq-client-view-card__stat">
                <span aria-hidden="true">🏠</span> {fmt(view.totalListings)} listings
              </p>
              <p className={`mq-client-view-card__stat${missing > 0 ? " mq-client-view-card__stat--warn" : ""}`}>
                <span aria-hidden="true">🖼️</span> {fmt(missing)} missing media
              </p>
              <p className={`mq-client-view-card__stat${view.notCardEligible > 0 ? " mq-client-view-card__stat--warn" : ""}`}>
                <span aria-hidden="true">📉</span> {fmt(view.notCardEligible)} not card-ready
              </p>
              <p className="mq-client-view-card__stat">
                <span aria-hidden="true">⭐</span> Avg {view.avgQuality != null ? Number(view.avgQuality).toFixed(1) : "—"}
              </p>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function IssueStatusBadges({ item }) {
  const badges = [];
  if (item.isBroken) badges.push({ emoji: "💔", label: "Broken", tone: "bad" });
  if (item.isDuplicate) badges.push({ emoji: "👯", label: "Duplicate", tone: "warn" });
  if (item.isLikelyPlaceholder) badges.push({ emoji: "🎭", label: "Placeholder", tone: "warn" });
  if (item.isPrimary) badges.push({ emoji: "⭐", label: "Primary", tone: "good" });
  if (item.isCardEligible === false) badges.push({ emoji: "📉", label: "Not card-ready", tone: "warn" });
  if (item.isCached) badges.push({ emoji: "☁️", label: "Cached", tone: "good" });
  if (!badges.length && item.qualityScore == null) {
    badges.push({ emoji: "❓", label: "Unscored", tone: "neutral" });
  }

  return (
    <div className="mq-issue-badges">
      {badges.map((badge) => (
        <span key={badge.label} className={`mq-issue-badge mq-issue-badge--${badge.tone}`}>
          <span aria-hidden="true">{badge.emoji}</span> {badge.label}
        </span>
      ))}
    </div>
  );
}

function IssueTable({
  items,
  loading,
  total,
  page,
  pageSize,
  onPageChange,
  onRecheck,
  onRescore,
  actionLoading,
}) {
  const totalPages = Math.ceil((total || 0) / pageSize);

  if (loading) {
    return <CardLoading label="Loading issues…" />;
  }

  if (!items?.length) {
    return <p className="mq-empty">No issues in this category. Try another filter above.</p>;
  }

  return (
    <>
      <div className="mq-issues__table-wrap">
        <table className="mq-issues__table">
          <thead>
            <tr>
              <th>Preview</th>
              <th>Listing</th>
              <th>Quality</th>
              <th>Flags</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const gradeMeta = resolveGradeMeta(item.qualityGrade);
              return (
                <tr key={item.mediaId || item.canonicalListingId || item.propertyId}>
                  <td>
                    <div className="mq-thumb">
                      {(item.imageUrl || item.currentImageUrl) && !item.isBroken ? (
                        <img
                          src={item.imageUrl || item.currentImageUrl}
                          alt=""
                          loading="lazy"
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <span className="mq-thumb__placeholder" aria-hidden="true">
                          🖼️
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="mq-issue__title">{item.title || item.address || "Untitled"}</div>
                    {item.address && item.title ? (
                      <div className="mq-issue__meta">{item.address}</div>
                    ) : null}
                    <div className="mq-issue__meta">{item.sourceKey || item.sourceCode || "—"}</div>
                  </td>
                  <td>
                    {item.qualityScore != null ? (
                      <span className={`mq-grade mq-grade--${gradeMeta.tone}`}>
                        <span aria-hidden="true">{gradeMeta.emoji}</span>
                        {Number(item.qualityScore).toFixed(0)} · {gradeMeta.label}
                      </span>
                    ) : (
                      <span className="mq-grade mq-grade--neutral">❓ Unscored</span>
                    )}
                    {item.lastCheckedAt ? (
                      <div className="mq-issue__meta">
                        Checked {new Date(item.lastCheckedAt).toLocaleDateString()}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <IssueStatusBadges item={item} />
                  </td>
                  <td>
                    <div className="mq-issue__actions">
                      {item.mediaId && onRecheck ? (
                        <AdminBtn
                          variant="ghost"
                          disabled={actionLoading}
                          onClick={() => onRecheck(item.mediaId)}
                        >
                          ↻ Recheck
                        </AdminBtn>
                      ) : null}
                      {item.mediaId && onRescore ? (
                        <AdminBtn
                          variant="ghost"
                          disabled={actionLoading}
                          onClick={() => onRescore(item.mediaId)}
                        >
                          ⭐ Score
                        </AdminBtn>
                      ) : null}
                      {item.imageUrl || item.originalUrl ? (
                        <a
                          href={item.imageUrl || item.originalUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="mq-issue__link"
                        >
                          Open ↗
                        </a>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <AdminTableFooter
        count={total}
        countLabel="issues"
        page={page}
        totalPages={totalPages}
        onPrevious={page > 1 ? () => onPageChange(page - 1) : undefined}
        onNext={page < totalPages ? () => onPageChange(page + 1) : undefined}
      />
    </>
  );
}

const MediaQualityDashboardPage = ({
  onClose,
  onOpenImageHealth,
  onOpenImageCache,
  onOpenSourceQuality,
  onOpenLocationQuality,
  onOpenCanonicalListings,
  onOpenGeocodeQueue,
}) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState("missing-media");
  const [issues, setIssues] = useState({ items: [], total: 0 });
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [filters, setFilters] = useState({
    page: 1,
    pageSize: 25,
    sourceCode: "",
    qualityGrade: "",
    search: "",
  });

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getAdminMediaQualityDashboard();
      setDashboard(data);
    } catch (err) {
      setError(err.message || "Unable to load media quality dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadIssues = useCallback(async () => {
    try {
      setIssuesLoading(true);
      const data = await getAdminMediaQualityIssues(activeTab, filters);
      setIssues({ items: data.items || [], total: data.total || 0 });
    } catch (err) {
      setIssues({ items: [], total: 0 });
      setToast({ message: `Failed to load issues: ${err.message}`, tone: "error" });
    } finally {
      setIssuesLoading(false);
    }
  }, [activeTab, filters]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  const handleRecheck = async (mediaId) => {
    try {
      setActionLoading(true);
      await recheckAdminMediaItem(mediaId);
      setToast({ message: "Image rechecked.", tone: "success" });
      await Promise.all([loadIssues(), loadDashboard()]);
    } catch (err) {
      setToast({ message: err.message, tone: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRescore = async (mediaId) => {
    try {
      setActionLoading(true);
      await rescoreAdminMediaItem(mediaId);
      setToast({ message: "Image quality rescored.", tone: "success" });
      await Promise.all([loadIssues(), loadDashboard()]);
    } catch (err) {
      setToast({ message: err.message, tone: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFilters((prev) => ({ ...prev, page: 1 }));
    document.querySelector(".mq-issues")?.scrollIntoView({ behavior: "smooth" });
  };

  const navLinks = useMemo(
    () =>
      buildRealEstateAdminNavLinks({
        current: "media-quality",
        onOpenSourceQuality,
        onOpenLocationQuality,
        onOpenCanonicalListings,
        onOpenGeocodeQueue,
        onOpenImageHealth,
        onOpenImageCache,
        onOpenMediaQualityDashboard: () => {},
      }),
    [
      onOpenSourceQuality,
      onOpenLocationQuality,
      onOpenCanonicalListings,
      onOpenGeocodeQueue,
      onOpenImageHealth,
      onOpenImageCache,
    ]
  );

  const sources = dashboard?.sourceBreakdown || [];

  return (
    <AdminPageLayout className="media-quality-dashboard">
      <AdminPageHeader
        eyebrow="Administration · Real Estate"
        title="🖼️ Media Quality"
        description="See which listing photos are ready for CasaMapa cards — and which need attention."
        onBack={onClose}
        navigation={<RealEstateAdminNav links={navLinks} />}
        maintenanceActions={
          <AdminBtn variant="secondary" onClick={loadDashboard} disabled={loading}>
            {loading ? "Loading…" : "↻ Refresh"}
          </AdminBtn>
        }
      />

      {toast ? (
        <p
          className={`admin-toast admin-toast--${toast.tone}`}
          role="status"
          onClick={() => setToast(null)}
        >
          {toast.message}
        </p>
      ) : null}

      {loading && !dashboard ? <CardLoading label="Loading media quality…" /> : null}
      {error ? <CardError message={error} /> : null}

      {!loading && !error && dashboard ? (
        <>
          <HealthBanner
            summary={dashboard.summary}
            primaryImageIssues={dashboard.primaryImageIssues}
            onOpenImageHealth={onOpenImageHealth}
            onOpenImageCache={onOpenImageCache}
          />

          <KpiStrip
            summary={dashboard.summary}
            primaryImageIssues={dashboard.primaryImageIssues}
            activeTab={activeTab}
            onSelectTab={handleTabChange}
          />

          <QualityDistribution distribution={dashboard.qualityDistribution} />

          <section className="mq-filters panel-card mq-issues">
            <h2 className="mq-section-title">
              <span aria-hidden="true">🔎</span> What do you want to fix?
            </h2>
            <div className="mq-filters__presets" role="tablist" aria-label="Issue categories">
              {ISSUE_PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === preset.key}
                  className={`mq-filter-chip${activeTab === preset.key ? " is-active" : ""}`}
                  onClick={() => handleTabChange(preset.key)}
                >
                  <span className="mq-filter-chip__emoji" aria-hidden="true">
                    {preset.emoji}
                  </span>
                  <span className="mq-filter-chip__text">
                    <strong>{preset.label}</strong>
                    <span>{preset.description}</span>
                  </span>
                </button>
              ))}
            </div>

            <div className="mq-filters__row">
              <label className="mq-filters__field">
                <span>Broker source</span>
                <select
                  value={filters.sourceCode || ""}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, sourceCode: event.target.value, page: 1 }))
                  }
                >
                  <option value="">All sources</option>
                  {sources.map((source) => {
                    const key = source.sourceKey || source.sourceCode;
                    return (
                      <option key={key} value={key}>
                        {key}
                      </option>
                    );
                  })}
                </select>
              </label>
              <label className="mq-filters__field">
                <span>Quality grade</span>
                <select
                  value={filters.qualityGrade || ""}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, qualityGrade: event.target.value, page: 1 }))
                  }
                >
                  <option value="">All grades</option>
                  {Object.entries(GRADE_META).map(([key, meta]) => (
                    <option key={key} value={key}>
                      {meta.emoji} {meta.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="mq-filters__field mq-filters__field--grow">
                <span>Search</span>
                <input
                  type="search"
                  placeholder="Title or address…"
                  value={filters.search || ""}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, search: event.target.value, page: 1 }))
                  }
                />
              </label>
            </div>

            <IssueTable
              items={issues.items}
              loading={issuesLoading}
              total={issues.total}
              page={filters.page}
              pageSize={filters.pageSize}
              onPageChange={(nextPage) => setFilters((prev) => ({ ...prev, page: nextPage }))}
              onRecheck={handleRecheck}
              onRescore={handleRescore}
              actionLoading={actionLoading}
            />
          </section>

          <SourceBreakdown
            sources={sources}
            onFilterSource={(sourceCode) =>
              setFilters((prev) => ({ ...prev, sourceCode, page: 1 }))
            }
          />

          <ClientViewBreakdown clientViews={dashboard.clientViewBreakdown} />
        </>
      ) : null}
    </AdminPageLayout>
  );
};

export default MediaQualityDashboardPage;
